import React, { useEffect, useState, useMemo } from 'react';
import { isNil, isPlainObject, isEqual } from 'lodash';
import { Form, Radio, Row, Col, FormItemProps, RadioGroupProps as AntRadioGroupProps } from 'antd';
import { Formatter } from '../utils';
import { transformRules, Rule } from '../utils/rules';
import InfoTooltip from './InfoTooltip';

interface FormRadioGroupProps {
  name: FormItemProps['name'];
  label?: string;
  tooltip?: PropPresets.Tooltip;
  disabled?: boolean;
  rules?: Rule[];
  options: PropPresets.Options;
  formItemProps?: FormItemProps;
  groupProps?: AntRadioGroupProps;
  onChange?: (val: string | number) => void;
  colSpan?: number;
  optionType?: 'button' | 'default';
}

function FormRadioGroup(props: FormRadioGroupProps) {
  const {
    name,
    label,
    disabled,
    formItemProps,
    tooltip,
    colSpan = 6,
    onChange,
    groupProps,
    rules: propRules,
    options: propOptions,
    optionType = 'default',
  } = props;

  const [options, setOptions] = useState<PropPresets.OptionObject[]>([]);

  const rules = useMemo(() => transformRules({ type: 'array' }, propRules), [propRules]);

  useEffect(() => {
    let formattedOps: PropPresets.OptionObject[] = [];
    if (!isNil(propOptions[0]) && !isPlainObject(propOptions[0])) {
      formattedOps = Formatter.makeOptions(propOptions as string[] | number[], true);
    }
    else {
      formattedOps = propOptions as PropPresets.OptionObject[];
    }
    if (!isEqual(options, formattedOps)) setOptions(formattedOps);
  }, [propOptions, options]);

  const RadioRow = useMemo(
    () => (
      <Row>
        {options.map(({ label: radioLabel, value, ...rest }) => {
          if (optionType === 'button') {
            return (
              <Radio.Button value={value} {...rest}>
                {radioLabel}
              </Radio.Button>
            );
          }
          return (
            <Col key={String(value)} span={colSpan} md={colSpan} xs={24}>
              <Radio value={value} style={{ margin: '5px 0' }} {...rest}>
                {radioLabel}
              </Radio>
            </Col>
          );
        })}
      </Row>
    ),
    [colSpan, optionType, options],
  );

  return (
    <Form.Item
      name={name}
      label={label}
      rules={rules}
      tooltip={InfoTooltip.Config(tooltip)}
      validateFirst
      {...formItemProps}
    >
      <Radio.Group
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        {...groupProps}
        style={{ width: '100%', ...groupProps?.style }}
      >
        {RadioRow}
      </Radio.Group>
    </Form.Item>
  );
}

export default FormRadioGroup;
